import { FontWeights, IButtonStyles, ITheme } from '@fluentui/react';
import {
  IChoiceNodeStyles,
  IGraphHeaderStyles,
  IInputNodeStyles,
  INodeHeaderStyles,
  INodeListStyles,
  INodeStyles,
  IRootContainerStyles,
} from '@ems/yoda-ux';
import { IDafNodeStyles, ISafActionNodeStyles } from '@ems/yoda-ux/dist/types';

export const getMainAppStyles = (theme: ITheme): Partial<IRootContainerStyles> => {
  return {
    root: {
      width: '100%',
      boxSizing: 'border-box',
      height: '100%',
      minWidth: '175px',
      minHeight: '200px',
      marginBottom: '20px',
      justifyContent: 'center',
      fontFamily: "'Segoe UI', 'Open Sans', Roboto, 'Helvetica Neue', sans-serif",
      fontSize: '14px',
      color: theme.semanticColors.bodyText,
      background: theme.palette.neutralLighterAlt,
      selectors: {
        a: {
          textDecoration: 'none',
          color: theme.palette.accent,
        },
      },
    },
  };
};
export const getNodeListStyles = (theme: ITheme): Partial<INodeListStyles> => {
  return {
    startNodeContainer: {
      width: '100%',
      overflow: 'hidden',
      background: theme.palette.neutralLighterAlt,
    },
    container: {
      background: theme.palette.neutralLighterAlt,
      width: '100%',
      overflow: 'hidden',
      marginBottom: '5px',
    },
    listContainer: {
      height: '100%',
      width: '100%',
      '& > *': {
        marginBottom: '15px',
      },
    },
    textNodeContainer: {
      background: theme.palette.neutralLighterAlt,
      overflow: 'hidden',
    },
  };
};

export const getNodeHeaderStyles = (theme: ITheme): Partial<INodeHeaderStyles> => {
  return {
    root: {
      background: theme.palette.neutralLighterAlt,
      color: theme.semanticColors.accentButtonText,
      display: 'flex',
      paddingRight: '0',
      position: 'relative',
      border: `1px solid ${theme.palette.themePrimary}`,
      borderRadius: '4px',
    },
    indexNumber: {
      'padding-right': '5px',
    },
    textDiv: {
      width: '80%',
      overflow: 'hidden',
    },
    nodeLabel: {
      fontSize: '14px',
      '& :first-child': {
        'margin-top': '0',
      },
      '& :last-child': {
        'margin-bottom': '0',
      },
    },
    chosenOptionLabel: {
      '& :first-child': {
        'margin-top': '0',
      },
      '& :last-child': {
        'margin-bottom': '0',
      },
      fontWeight: FontWeights.regular,
      'overflow-wrap': 'break-word',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    editIcon: {
      cursor: 'pointer',
      float: 'right',
    },
    completedIcon: {
      cursor: 'pointer',
      float: 'right',
    },
    failedIcon: {
      cursor: 'pointer',
      float: 'right',
    },
  };
};

export const getNodeStyles = (theme: ITheme): Partial<INodeStyles> => {
  return {
    startNodeLabel: {
      fontWeight: FontWeights.regular,
      background: theme.palette.neutralLighterAlt,
      color: theme.palette.black,
      display: 'block',
      lineHeight: '20px',
      fontSize: '14px',
      marginTop: '10px',
      '& :first-child': {
        'margin-top': '0',
      },
    },
    hairline: {
      width: '100%',
      marginTop: '25px',
      background: theme.palette.neutralTertiaryAlt,
      height: '1px',
      borderWidth: '0px',
    },
    contentTitle: {
      fontSize: '16px',
      fontWeight: '600',
      lineHeight: '22px',
      maxHeight: '44px',
      overflow: 'hidden',
      marginBottom: '8px',
    },
    content: {
      color: theme.palette.black,
      background: theme.palette.neutralLighterAlt,
      overflow: 'hidden',
      width: '90%',
      '& a': {
        textDecoration: 'none',
        color: theme.palette.themeDark,
      },
      '& *': {
        marginTop: '0px',
        marginBottom: '5px',
      },
      '& ol': {
        padding: '0px 0px 0px 20px',
      },
      '& ul': {
        padding: '0px 0px 0px 20px',
      },
      '& li': {
        padding: '13px 0px 3px 0px',
      },
      // overflow for li will hide numbers unless we want to show the numbers inside
      '& :not(li)': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        background: theme.palette.neutralLighterAlt,
        color: theme.palette.black,
      },
      '.content:after': {
        content: '',
        textAlign: 'right',
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '70%',
        maxHeight: '1.2em',
        background: theme.palette.neutralLighterAlt,
      },
    },
    question: {
      color: theme.palette.black,
      background: theme.palette.neutralLighterAlt,
      width: '80%',
      fontWeight: FontWeights.semibold,
    },
    nodeWithSecondaryText: {
      background: theme.palette.neutralLighterAlt,
      color: theme.palette.black,
      border: '1px solid transparent',
    },
  };
};

export const getOptionBtnStyles = (theme: ITheme): Partial<IButtonStyles> => {
  return {
    root: {
      background: theme.palette.neutralLight,
      color: theme.palette.black,
      border: `1px solid ${theme.palette.neutralLight}`,
      borderRadius: '4px',
      marginTop: '16px',
      marginLeft: '0px',
      minWidth: '52px',
      minHeight: '42px',
      width: '100%',
    },
    rootChecked: {
      background: theme.semanticColors.accentButtonBackground, // theme.palette.themePrimary
      color: theme.semanticColors.accentButtonText,
    },
    rootHovered: {
      background: theme.palette.neutralQuaternary,
    },
    label: {
      fontWeight: FontWeights.regular,
      overflow: 'hidden',
      height: 'auto',
      width: '100%',
      marginLeft: '0px',
      '& > *': {
        fontWeight: FontWeights.regular,
        overflow: 'hidden',
        'overflow-wrap': 'break-word',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        minHeight: '18px !important',
        marginLeft: '0px',
      },
      'overflow-wrap': 'break-word',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      minHeight: '18px !important',
    },
    textContainer: {
      textAlign: 'left',
      overflow: 'hidden',
      marginLeft: '0px',
    },
  };
};

export const getChoiceNodeStyles = (theme: ITheme): Partial<IChoiceNodeStyles> => {
  return {
    container: {
      width: '100%',
      border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
      overflow: 'hidden',
      marginBottom: '5px',
      marginLeft: '0px',
    },
    listContainer: {
      margin: '0px',
    },
    choicesList: {
      margin: '0px',
    },
    btnLabel: {
      fontWeight: FontWeights.regular,
      overflow: 'hidden',
      height: 'auto',
      width: '100%',
      '& > *': {
        fontWeight: FontWeights.regular,
        overflow: 'hidden',
        'overflow-wrap': 'break-word',
        margin: '0px',
      },
    },
    choiceBtn: getOptionBtnStyles(theme),
  };
};

export const getHeaderStyles = (theme: ITheme): Partial<IGraphHeaderStyles> => {
  return {
    headerRootDiv: {
      width: '100%',
      '& > *': {
        width: '100%',
        fontSize: '15px',
        color: theme.palette.black,
        fontWeight: '600',
        overflow: 'auto',
        'overflow-wrap': 'break-word',
      },
    },
  };
};

export const getDafSpinnerStyle = (theme: ITheme): Partial<IDafNodeStyles> => {
  return {
    completedIcon: {
      cursor: 'pointer',
      float: 'right',
    },
    failedIcon: {
      cursor: 'pointer',
      float: 'right',
    },
    label: {
      width: '100%',
      textAlign: 'left',
    },
    dafContainer: {
      margin: '0px',
    },
  };
};

export const getSafActionNodeStyles = (theme: ITheme): Partial<ISafActionNodeStyles> => {
  return {
    label: {
      fontWeight: 600,
      overflow: 'hidden',
      width: '100%',
      textAlign: 'left',
      '& > *': {
        fontWeight: 600,
        overflow: 'hidden',
        'overflow-wrap': 'break-word',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        textAlign: 'left',
      },
    },
    safContainer: {
      margin: '10px',
      marginTop: 0,
    },
    completedIcon: {
      cursor: 'pointer',
      float: 'right',
    },
    inlineDiagnostics: {
      contentContainer: {
        marginBottom: '16px',
        width: '80%',
      },
      diagnostcForm: {
        button: {
          root: {
            background: theme.semanticColors.accentButtonBackground,
            marginRight: '16px',
            marginTop: '16px',
            border: `1px solid ${theme.palette.themePrimary}`,
          },
          rootDisabled: {
            backgroundColor: theme.semanticColors.accentButtonBackground,
            borderColor: `1px solid ${theme.palette.themePrimary}`,
            color: theme.palette.themeLighterAlt,
            opacity: '.65',
            boxShadow: 'none',
            cursor: 'not-allowed',
            padding: '6px 16px',
            fontSize: '14px',
          },
        },
        common: {
          contentContainer: {
            marginBottom: '16px',
            width: '80%',
          },
          sectionHeader: {
            fontWeight: '600',
            fontSize: '14px',
            lineHeight: '20px',
          },
          title: {
            fontSize: '16px',
            fontWeight: '600',
          },
        },
      },
    },
  };
};

export const getInputNodeStyles = (theme: ITheme): Partial<IInputNodeStyles> => {
  return {
    button: {
      root: {
        background: theme.semanticColors.accentButtonBackground,
        marginRight: '16px',
        marginTop: '16px',
        border: `1px solid ${theme.palette.themePrimary}`,
      },
      rootDisabled: {
        backgroundColor: theme.semanticColors.accentButtonBackground,
        borderColor: `1px solid ${theme.palette.themePrimary}`,
        color: theme.palette.themeLighterAlt,
        opacity: '.65',
        boxShadow: 'none',
        cursor: 'not-allowed',
        padding: '6px 16px',
        fontSize: '14px',
      },
    },
  };
};

export const getGetHelpCustomStyles = (theme: ITheme) => () => {
  return {
    hostDiv: getMainAppStyles(theme),
    nodeList: getNodeListStyles(theme),
    nodeHeader: getNodeHeaderStyles(theme),
    node: getNodeStyles(theme),
    choice: getChoiceNodeStyles(theme),
    safNode: {},
    dafSpinner: getDafSpinnerStyle(theme),
    mainHeader: getHeaderStyles(theme),
    safActionNode: getSafActionNodeStyles(theme),
    inputNode: getInputNodeStyles(theme),
  };
};
